import axios, { AxiosInstance } from 'axios'
import { ChangeLogBody, JobStatus, Review, Role } from 'types/misc'
import { MISC_ENDPOINTS } from 'api/ApiRoutes'
import {
  Accountant,
  AccountantClient,
  AccountantClientDetail
} from 'types/accounting'
import { ID } from 'types/utility'
import { createLinkTokenResponse } from 'types/integrations'
import { InstitutionAccount, InstitutionUser } from 'types/institutions'
import { Vehicle } from 'types/vehicles'
import {
  FavoriteTransaction,
  FavoriteTransactionPayload
} from 'types/favorite-transactions'
import { formatDateParams } from 'utils/Functions'

class MiscApi {
  constructor(
    private axiosInstance: AxiosInstance,
    private axiosInstanceNoVersion: AxiosInstance
  ) {}

  getRoles = async () => {
    const d = await this.axiosInstance.get<Role[]>(MISC_ENDPOINTS.ROLES)
    return d.data
  }

  getAccountantGroup = async () => {
    const { data } = await this.axiosInstance.get<Accountant>(
      MISC_ENDPOINTS.ACCOUNTANT_GROUPS
    )
    return data
  }

  getJobStatus = ({ jobId }: { jobId: ID }) => {
    return this.axiosInstance
      .get<JobStatus>(MISC_ENDPOINTS.JOB_STATUS(jobId))
      .then((d) => d.data)
  }

  getInstitutionToken = (token_id: ID) => {
    return this.axiosInstance
      .post(MISC_ENDPOINTS.INSTITUTION_TOKEN(token_id))
      .then((d) => d.data)
  }

  createLinkToken = async ({
    token_id = null,
    redirect_uri,
    direct_deposit
  }: {
    token_id?: ID | null
    redirect_uri?: string
    direct_deposit?: boolean
  }) => {
    const payload: Record<string, ID | string | boolean | null | undefined> = {}
    payload.redirect_uri = redirect_uri
    if (token_id) {
      payload.institution_user_token_id = token_id
    }
    if (typeof direct_deposit === 'boolean') {
      payload.direct_deposit = direct_deposit
    }

    const d = await this.axiosInstance.post<createLinkTokenResponse>(
      MISC_ENDPOINTS.INSTITUTION_USER_CREATE_LINK_TOKEN,
      payload
    )
    return d.data
  }

  createInstitutionUser = async ({
    public_token,
    institution_id,
    import_history
  }: {
    public_token: string
    institution_id: string
    import_history: 'all' | 'last30' | 'none'
  }) => {
    const data = {
      public_token,
      institution_id,
      import_history
    }
    const d = await this.axiosInstance.post<
      Record<'institution_user', InstitutionUser>
    >(MISC_ENDPOINTS.INSTITUTION_USERS, data)
    return d.data.institution_user
  }

  updateInstitutionAccount = async ({
    token_id,
    excluded
  }: {
    token_id: ID
    excluded: boolean
  }) => {
    const data = {
      institution_account: {
        excluded
      }
    }
    const d = await this.axiosInstance.put<InstitutionAccount>(
      MISC_ENDPOINTS.INSTITUTION_ACCOUNT(token_id),
      data
    )
    return d.data
  }

  unlinkInstitutionUser = (token_id: ID) => {
    return this.axiosInstance.delete(MISC_ENDPOINTS.INSTITUTION_USER(token_id))
  }

  refreshInstitution = async () => {
    const d = await this.axiosInstance.get(
      MISC_ENDPOINTS.INSTITUTION_USERS_SYNC
    )
    return d.data
  }

  updateInstitutionLogin = async (token_id: ID) => {
    const d = await this.axiosInstance.post(
      MISC_ENDPOINTS.INSTITUTION_USER_PUBLIC_TOKEN(token_id)
    )
    return d.data
  }

  createReview = async (stars: number) => {
    const d = await this.axiosInstance.post<Review>(MISC_ENDPOINTS.REVIEWS, {
      review: {
        stars
      }
    })
    return d.data
  }

  getReviews = async ({ pageSize }: { pageSize: number }) => {
    const d = await this.axiosInstance.get<{
      internal: Review[]
      g2: Review[]
    }>(`${MISC_ENDPOINTS.REVIEWS}?page_size=${pageSize}`)
    return d.data
  }

  g2Reviews = async () => {
    const d = await this.axiosInstance.get<{ url: string }>(
      MISC_ENDPOINTS.REVIEWS_G2
    )
    return d.data
  }

  g2ReviewMarkAsDone = async () => {
    const d = await this.axiosInstance.put(MISC_ENDPOINTS.REVIEWS_G2_MARK_DONE)
    return d.data
  }

  g2ReviewNotShowAgain = async () => {
    const d = await this.axiosInstance.put(MISC_ENDPOINTS.REVIEWS_G2_NOT_SHOW)
    return d.data
  }

  importHistory = async ({
    source,
    entity,
    file
  }: {
    source: 'everlance' | 'miq' | 'qbse'
    entity: 'trips' | 'transactions'
    file: File
  }) => {
    const formData = new FormData()
    formData.append('source', source)
    formData.append('entity', entity)
    formData.append('file', file)
    const d = await this.axiosInstance.post<{
      failed_instances: number
      successful_instances: number
    }>(MISC_ENDPOINTS.BULK_IMPORTS, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return d.data
  }

  createVehicle = async ({ vehicle }: { vehicle: Partial<Vehicle> }) => {
    const d = await this.axiosInstance.post(MISC_ENDPOINTS.VEHICLES, vehicle)
    return d.data
  }

  updateVehicle = async ({
    vehicle,
    vehicleId
  }: {
    vehicleId: Vehicle['token_id']
    vehicle: Partial<Vehicle>
  }) => {
    const d = await this.axiosInstance.put(
      MISC_ENDPOINTS.VEHICLE(vehicleId),
      vehicle
    )
    return d.data
  }

  deleteVehicle = async ({ vehicleId }: { vehicleId: Vehicle['token_id'] }) => {
    const d = await this.axiosInstance.delete(MISC_ENDPOINTS.VEHICLE(vehicleId))
    return d.data
  }

  sendLinkToText = async ({ number }: { number: string }) => {
    const d = await this.axiosInstanceNoVersion.post(MISC_ENDPOINTS.SEND_TEXT, {
      number
    })
    return d.data
  }

  createFavoriteTransaction = async ({
    payload
  }: {
    payload: FavoriteTransactionPayload
  }) => {
    const d = await this.axiosInstance.post(
      MISC_ENDPOINTS.FAVORITE_TRANSACTIONS,
      {
        favorite_transaction: {
          ...payload,
          scope: 'transactions'
        }
      }
    )
    return d.data
  }

  updateFavoriteTransaction = async ({
    tokenId,
    payload
  }: {
    tokenId: FavoriteTransaction['id']
    payload: FavoriteTransactionPayload
  }) => {
    const d = await this.axiosInstance.put(
      MISC_ENDPOINTS.FAVORITE_TRANSACTION(tokenId),
      {
        favorite_transaction: {
          ...payload,
          scope: 'transactions'
        }
      }
    )
    return d.data
  }
  deleteFavoriteTransaction = async ({
    tokenId
  }: {
    tokenId: FavoriteTransaction['id']
  }) => {
    const d = await this.axiosInstance.delete(
      MISC_ENDPOINTS.FAVORITE_TRANSACTION(tokenId)
    )
    return d.data
  }

  getAccountantClientDetail = async ({
    clientId,
    dateRange
  }: {
    clientId: AccountantClient['token_id']
    dateRange: [string, string]
  }) => {
    const d = await this.axiosInstance.get<AccountantClientDetail>(
      MISC_ENDPOINTS.ACCOUNTANT_CLIENT(clientId),
      {
        params: {
          ...formatDateParams(dateRange)
        }
      }
    )
    return d.data
  }

  exportAccountantClient = async ({
    clientId,
    dateRange
  }: {
    clientId: AccountantClient['token_id']
    dateRange: [string, string]
  }) => {
    const d = await this.axiosInstance.post<AccountantClientDetail>(
      MISC_ENDPOINTS.EXPORT_ACCOUNTANT_CLIENT(clientId),
      {
        ...formatDateParams(dateRange)
      }
    )
    return d.data
  }

  updateAccountProfile = async ({
    data
  }: {
    data: {
      first_name: Accountant['first_name']
      last_name: Accountant['last_name']
      email: Accountant['email']
      company_name: Accountant['company_name']
      description: Accountant['description']
      phone_number: Accountant['phone_number']
      photo: Accountant['photo'] | File
    }
  }) => {
    const formData = new FormData()
    if (!data.photo) {
      formData.append('accountant_group[photo_attributes][image]', '')
    }

    if (data?.photo && !(data?.photo as Accountant['photo'])?.image_url) {
      formData.append(
        'accountant_group[photo_attributes][image]',
        data.photo ? (data.photo as File) : ''
      )
    }

    formData.append('accountant_group[first_name]', data?.first_name ?? '')
    formData.append('accountant_group[last_name]', data?.last_name ?? '')
    formData.append('accountant_group[email]', data?.email ?? '')
    formData.append('accountant_group[company_name]', data?.company_name ?? '')
    formData.append('accountant_group[description]', data?.description ?? '')
    formData.append('accountant_group[phone_number]', data?.phone_number ?? '')
    const d = await this.axiosInstance.put<Accountant>(
      MISC_ENDPOINTS.ACCOUNTANT_CLIENT_FIRST,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )
    return d.data
  }

  reconnectInstitution = ({ token_id }: { token_id: string }) => {
    // token_id being the institution user token id
    return this.axiosInstance.post(
      MISC_ENDPOINTS.RECONNECT_INSTITUTION(token_id)
    )
  }

  getChangelog = () => {
    return axios
      .get<ChangeLogBody>(
        `https://dashboard-changelog.s3.us-east-1.amazonaws.com/changelog.json?v=${Date.now()}`
      )
      .then(({ data }) => data)
  }
}

export default MiscApi
